import Bitmap from "../bitmap";
import { IGame } from "../types/game";
import Block from "../models/block";
import { ICanvas } from "../types/canvas";
import Color from "../models/color";
import IPlayer from "../models/player";
import { getBlockPackFromStorage } from "../packs/blocks/BlockPackFactory";

export interface IBlockQueueView {
  draw(): HTMLCanvasElement;
}

export default class BlockQueueView implements IBlockQueueView {
  width: number;
  height: number;
  player: IPlayer;
  game: IGame;
  canvas: ICanvas;
  screen: HTMLCanvasElement;
  context: CanvasRenderingContext2D;
  blockSize: number;

  constructor(screen: HTMLCanvasElement, game: IGame, width: number, blockSize: number) {
    this.game = game;
    this.canvas = game.canvas;
    this.player = game.player;
    this.screen = screen;
    this.context = screen.getContext("2d");
    if (this.context) {
      // Disable image smoothing for crisp pixel rendering
      this.context.imageSmoothingEnabled = false;
      this.context.imageSmoothingQuality = "low";
    }
    this.blockSize = blockSize;
    this.width = this.screen.width = width;
    // Height needs to accommodate next3 which extends 18px (1 blockSize) above its zone
    // Original: next3 at y=0 draws at y=-18, so we need at least blockSize padding at top
    this.height = this.screen.height = this.blockSize * 3.5;
  }

  public draw(): HTMLCanvasElement {
    this.context.clearRect(0, 0, this.width, this.height);
    // this.drawBackground();

    // Only show blocks if game allows rendering (handles multiplayer countdown)
    if (this.game.shouldRenderPieces()) {
      // Match original layout from zone.cc lines 82-84:
      // - next (bottom): small size (6px = blockSize/3), zone at x+5, y=2, draws at y-6
      // - next2 (middle): small size (6px = blockSize/3), zone at x+32, y=8, draws at y-6
      // - next3 (top, next to use): full size (18px = blockSize), zone at x+60, y=0, draws at y-18
      // Scale factor: original uses 18px blocks, we use this.blockSize
      const scale = this.blockSize / 18;
      
      // Original absolute positions (scaled to our blockSize):
      // These positions are relative to canvas x, but we'll position them in our queue view
      // We'll use left-aligned positioning with proper spacing
      // Add top padding to accommodate blocks that extend above their zones
      const topPadding = this.blockSize;
      
      if (this.canvas.next) {
        // next: small size, positioned at left
        this.drawBlock(this.canvas.next, false, 5 * scale, topPadding + 2 * scale - 6 * scale);
      }
      if (this.canvas.next2) {
        // next2: small size, positioned in middle-left
        this.drawBlock(this.canvas.next2, false, 32 * scale, topPadding + 8 * scale - 6 * scale);
      }
      if (this.canvas.next3) {
        // next3: full size, positioned at right
        this.drawBlock(this.canvas.next3, true, 60 * scale, topPadding + 0 - 18 * scale);
      }
    }

    return this.screen;
  }

  private drawBlock(
    block: Block, 
    isLarge: boolean,
    offsetX: number, 
    drawY: number
  ): void {
    // next3 uses full size (blockSize), next and next2 use small size (blockSize/3)
    const blockRenderSize = isLarge ? this.blockSize : this.blockSize / 3;
    
    const pack = getBlockPackFromStorage();
    const bitmap = new Bitmap(this.context);
    
    // Original code draws blocks starting from the top-left of their 4x4 grid
    // No centering - just draw at the given position like bloc.cc:draw() does
    // It iterates through the 4x4 grid and draws each brick at tx+i*18, ty+j*18
    // So we draw at offsetX, drawY and let each brick position itself within the 4x4 grid
    
    block.getBlueprint().forEach((brick) => {
      pack.drawBlock(
        bitmap,
        Math.ceil(offsetX + brick.x * blockRenderSize),
        Math.ceil(drawY + brick.y * blockRenderSize),
        brick.t,
        Math.ceil(blockRenderSize),
        new Color(brick.color, this.game.palette),
        brick.to
      );
    });
  }

  private drawBackground(): void {
    let context: CanvasRenderingContext2D = this.context;
    context.fillStyle = "rgba(0,0,0,0.2)";
    context.fillRect(0, 0, this.width, this.height);
    context.strokeStyle = "#FFF";
    context.strokeRect(0, 0, this.width, this.height);
  }
}
